import React, { useState } from 'react';
import Modal from '../../components/shared/Modal/Modal';
import Button from '../../components/shared/Button/Button';
import { useConfirm } from '../../components/shared/ConfirmDialog/ConfirmDialog';
import {
  FormGroup,
  FormLabel,
  FormInput,
  FormSelect,
  FormHint,
  FormGrid,
  FormActions,
  ErrorMessage,
  Switch,
  SwitchGroup,
} from '../../components/shared/Form/Form.styles';
import { downloadSalesExport, saveBlob } from '../../services/salesApi';
import { daysBetween, shiftCalendarDate, todayInMexico } from '../../utils/dates';
import { MethodGroup } from './Sell.styles';
import { Notice } from './Sales.styles';
import { WarningPanel } from './SalesList.styles';

const MAX_RANGE_DAYS = 366;

// Exportar ventas a CSV o Excel (solo dueño). El CURP NO se incluye por defecto:
// activarlo pide confirmar un aviso de privacidad, y el interruptor vuelve a
// apagarse cada vez que se abre el modal.
const ExportModal = ({ activities, defaultActivityId = '', onClose }) => {
  const today = todayInMexico();
  const [form, setForm] = useState({
    from: shiftCalendarDate(today, -30),
    to: today,
    format: 'csv',
    source: 'all',
    activityId: defaultActivityId,
    basis: 'sale', // fecha de venta (en hora de México); 'activity' = fecha de la actividad
  });
  const [includeCurp, setIncludeCurp] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState('');
  const [busy, setBusy] = useState(false);
  const { confirm, dialog } = useConfirm();

  const set = (key) => (event) => setForm({ ...form, [key]: event.target.value });
  const basisText = form.basis === 'activity' ? 'fecha de la actividad' : 'fecha de venta';

  const toggleCurp = async (checked) => {
    if (!checked) {
      setIncludeCurp(false);
      return;
    }
    const ok = await confirm({
      title: '¿Incluir el CURP en el archivo?',
      message: (
        <>
          <p>
            El archivo tendrá el <strong>CURP completo</strong> de cada asistente, sin enmascarar y sin cifrar.
            Es un dato personal: quien tenga el archivo podrá verlo.
          </p>
          <p>
            Descárgalo solo si lo necesitas, no lo compartas por canales abiertos (chats, correos sin protección),
            guárdalo en un lugar seguro y bórralo cuando ya no lo uses. Tú eres responsable de su uso.
          </p>
        </>
      ),
      confirmLabel: 'Entiendo, incluir CURP',
      danger: true,
    });
    if (ok) setIncludeCurp(true);
  };

  const validate = () => {
    if (!form.from || !form.to) return 'Elige el rango de fechas.';
    if (form.from > form.to) return 'La fecha inicial no puede ser posterior a la final.';
    if (daysBetween(form.from, form.to) + 1 > MAX_RANGE_DAYS) {
      return `El rango máximo es de ${MAX_RANGE_DAYS} días. Reduce las fechas.`;
    }
    return '';
  };

  const handleDownload = async (event) => {
    event.preventDefault();
    setError('');
    setDone('');
    const problem = validate();
    if (problem) {
      setError(problem);
      return;
    }
    setBusy(true);
    try {
      const { blob, filename } = await downloadSalesExport({
        format: form.format,
        from: form.from,
        to: form.to,
        source: form.source,
        activityId: form.activityId,
        basis: form.basis,
        includeCurp: includeCurp ? 'true' : undefined,
      });
      saveBlob(blob, filename);
      setDone(`Listo: se descargó ${filename}.${includeCurp ? ' Contiene CURP completos: guárdalo en un lugar seguro.' : ''}`);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Modal title="Exportar ventas" onClose={busy ? null : onClose} maxWidth="560px" labelledBy="export-title">
        <form onSubmit={handleDownload}>
          {error && <ErrorMessage role="alert">{error}</ErrorMessage>}
          {done && <Notice role="status">{done}</Notice>}

          <FormGroup>
            <FormLabel id="export-basis-label">Filtrar las fechas por</FormLabel>
            <MethodGroup role="radiogroup" aria-labelledby="export-basis-label">
              <label>
                <input type="radio" name="export-basis" value="sale" checked={form.basis === 'sale'} onChange={set('basis')} />
                <span>Fecha de venta</span>
              </label>
              <label>
                <input type="radio" name="export-basis" value="activity" checked={form.basis === 'activity'} onChange={set('basis')} />
                <span>Fecha de la actividad</span>
              </label>
            </MethodGroup>
            <FormHint>
              {form.basis === 'sale'
                ? 'Cuándo se hizo la venta, en hora de México. Es la que sirve para el corte de caja y para los cortes de promotores.'
                : 'El día en que se realiza la actividad (como se exportaba antes).'}
            </FormHint>
          </FormGroup>

          <FormGrid>
            <FormGroup>
              <FormLabel htmlFor="export-from">Desde ({basisText})</FormLabel>
              <FormInput id="export-from" type="date" value={form.from} onChange={set('from')} required />
            </FormGroup>
            <FormGroup>
              <FormLabel htmlFor="export-to">Hasta ({basisText})</FormLabel>
              <FormInput id="export-to" type="date" value={form.to} onChange={set('to')} required />
            </FormGroup>
          </FormGrid>
          <FormHint style={{ marginTop: -8, marginBottom: 14 }}>
            Máximo {MAX_RANGE_DAYS} días. Incluye ventas en línea y en persona; las anuladas salen con su estado.
            El archivo trae la fecha de venta y, si la venta fue de un promotor, su nombre, el porcentaje y la comisión.
          </FormHint>

          <FormGrid>
            <FormGroup>
              <FormLabel htmlFor="export-source">Origen</FormLabel>
              <FormSelect id="export-source" value={form.source} onChange={set('source')}>
                <option value="all">Todas</option>
                <option value="in_person">Solo en persona</option>
                <option value="online">Solo en línea</option>
              </FormSelect>
            </FormGroup>
            <FormGroup>
              <FormLabel htmlFor="export-activity">Actividad</FormLabel>
              <FormSelect id="export-activity" value={form.activityId} onChange={set('activityId')}>
                <option value="">Todas</option>
                {activities.map((a) => <option key={a.id} value={a.id}>{a.title}</option>)}
              </FormSelect>
            </FormGroup>
          </FormGrid>

          <FormGroup>
            <FormLabel id="export-format-label">Formato</FormLabel>
            <MethodGroup role="radiogroup" aria-labelledby="export-format-label">
              <label>
                <input type="radio" name="export-format" value="csv" checked={form.format === 'csv'} onChange={set('format')} />
                <span>CSV</span>
              </label>
              <label>
                <input type="radio" name="export-format" value="xlsx" checked={form.format === 'xlsx'} onChange={set('format')} />
                <span>Excel (.xlsx, con resumen)</span>
              </label>
            </MethodGroup>
          </FormGroup>

          <SwitchGroup>
            <Switch>
              <input
                type="checkbox"
                checked={includeCurp}
                disabled={busy}
                onChange={(e) => toggleCurp(e.target.checked)}
                aria-describedby="export-curp-warning"
                aria-label="Incluir CURP en el archivo"
              />
              <span className="slider"></span>
            </Switch>
            <div>
              <strong>Incluir CURP de los asistentes</strong>
              <p>Apagado por defecto.</p>
            </div>
          </SwitchGroup>
          <WarningPanel id="export-curp-warning" $active={includeCurp} role={includeCurp ? 'alert' : undefined}>
            {includeCurp ? (
              <>
                <strong>El archivo incluirá los CURP completos.</strong> Es información personal protegida: no la compartas por chat
                ni correo abierto, guárdala de forma segura y bórrala cuando ya no la necesites.
              </>
            ) : (
              <>
                <strong>Privacidad:</strong> el archivo no lleva CURP. Al activar esta opción verás un aviso y deberás confirmarla.
              </>
            )}
          </WarningPanel>

          <FormActions>
            <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>Cerrar</Button>
            <Button type="submit" disabled={busy}>
              {busy ? 'Generando…' : `Descargar ${form.format === 'xlsx' ? 'Excel' : 'CSV'}`}
            </Button>
          </FormActions>
        </form>
      </Modal>
      {dialog}
    </>
  );
};

export default ExportModal;
